import lineVs from "./shaders/lines/vs.glsl";
import lineFs from "./shaders/lines/fs.glsl";
import maskingMeshVs from "./shaders/masking_mesh/vs.glsl";
import maskingMeshFs from "./shaders/masking_mesh/fs.glsl";
import backgroundMeshVs from "./shaders/background_mesh/vs.glsl";
import backgroundMeshFs from "./shaders/background_mesh/fs.glsl";
import titleVs from "./shaders/title/vs.glsl";
import titleFs from "./shaders/title/fs.glsl";
import { useTexture } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useEffect, useMemo, useRef } from "react";
import gsap from "gsap";
import { useControls } from "leva";

const DEBUG = false;

const params = {
  initialAngle: Math.PI * 1.35,
  stage1Angle: Math.PI * 0.35,
  initialFocus: new THREE.Vector2(0.05, 0.1),
  moveFocus: new THREE.Vector2(0.02, -0.07),
  lineColor: "#38b2c7",
  initialColor: "#1a4c5c",
  gradientColor1: "#1e4652",
  gradientColor2: "#5a7e90",
  gradientFactor: 0.5,
};

const lines = {
  l0: {
    color: [1, 0, 0],
    initialAngle: params.initialAngle,
    stage1Angle: params.initialAngle,
    stage2Rotation: -Math.PI * 0.2,
    stage3Rotation: Math.PI * 0.4,
  },

  l1: {
    color: [1, 1, 0],
    initialAngle: params.initialAngle,
    stage1Angle: params.initialAngle,
    stage2Rotation: Math.PI * 0.4,
    stage3Rotation: Math.PI * 0.5,
  },

  l2: {
    color: [1, 0, 1],
    initialAngle: params.initialAngle,
    stage1Angle: params.stage1Angle,
    stage2Rotation: Math.PI * 0.12,
    stage3Rotation: Math.PI * 0.4,
  },

  l3: {
    color: [0, 0, 1],
    initialAngle: params.initialAngle,
    stage1Angle: params.stage1Angle,
    stage2Rotation: Math.PI * 0.36,
    stage3Rotation: Math.PI * 0.4,
  },

  l4: {
    color: [1, 1, 1],
    initialAngle: params.initialAngle,
    stage1Angle: params.stage1Angle,
    stage2Rotation: Math.PI * 0.36,
    stage3Rotation: Math.PI * 2.4,
  },
};

const Scene = () => {
  const { size } = useThree();

  const [colorText, outlineText] = useTexture(["./colortext.png", "./outlinetext.png"]);

  // Uniforms
  const uniforms = useRef({
    uPI: new THREE.Uniform(Math.PI),
    uSizes: new THREE.Uniform(new THREE.Vector2(size.width, size.height)),
    uFocus: new THREE.Uniform(params.initialFocus.clone()),
    uProgress1: new THREE.Uniform(0),
    uProgress2: new THREE.Uniform(0),
    uProgress3: new THREE.Uniform(0),
    uTickAngle: new THREE.Uniform(lines.l4.stage1Angle),
    uPointer: new THREE.Uniform(params.initialFocus.clone()),
    uLineColor: new THREE.Uniform(new THREE.Color(params.lineColor)),
    uInitialColor: new THREE.Uniform(new THREE.Color(params.initialColor)),
    uGradientColor1: new THREE.Uniform(new THREE.Color(params.gradientColor1)),
    uGradientColor2: new THREE.Uniform(new THREE.Color(params.gradientColor2)),
    uGradientFactor: new THREE.Uniform(params.gradientFactor),
    uTitleWidth: new THREE.Uniform(400),
    uTitleAspect: new THREE.Uniform(1892 / 712),
    uColorText: new THREE.Uniform(colorText),
    uOutlineText: new THREE.Uniform(outlineText),
    uLine0Angle: new THREE.Uniform(lines.l0.stage1Angle),
    uLine1Angle: new THREE.Uniform(lines.l1.stage1Angle),
    uLine2Angle: new THREE.Uniform(lines.l2.stage1Angle),
    uLine3Angle: new THREE.Uniform(lines.l3.stage1Angle),
  });

  const animation = useRef({
    completed: false,
    progress1: 0,
    progress2: 0,
    progress3: 0,
  });

  // Geometries
  const lineGeometry = useMemo(() => {
    let positions = [];
    let colors = [];
    let onCenter = [];
    let initialAngles = [];
    let stage1Angles = [];
    let stage2Rotations = [];
    let stage3Rotations = [];

    for (let [lineName, line] of Object.entries(lines)) {
      positions.push(0, 0, 0, 0, 0, 0);

      colors.push(...line.color, ...line.color);
      onCenter.push(0, 1);
      initialAngles.push(line.initialAngle, line.initialAngle);
      stage1Angles.push(line.stage1Angle, line.stage1Angle);
      stage2Rotations.push(line.stage2Rotation, line.stage2Rotation);
      stage3Rotations.push(line.stage3Rotation, line.stage3Rotation);
    }

    let geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(positions), 3)
    );
    geometry.setAttribute("aColor", new THREE.BufferAttribute(new Float32Array(colors), 3));
    geometry.setAttribute(
      "aOnCenter",
      new THREE.BufferAttribute(new Float32Array(onCenter), 1)
    );
    geometry.setAttribute(
      "aInitialAngle",
      new THREE.BufferAttribute(new Float32Array(initialAngles), 1)
    );
    geometry.setAttribute(
      "aStage1Angle",
      new THREE.BufferAttribute(new Float32Array(stage1Angles), 1)
    );
    geometry.setAttribute(
      "aStage2Rotation",
      new THREE.BufferAttribute(new Float32Array(stage2Rotations), 1)
    );
    geometry.setAttribute(
      "aStage3Rotation",
      new THREE.BufferAttribute(new Float32Array(stage3Rotations), 1)
    );

    return geometry;
  }, []);

  const meshGeometry = useMemo(() => {
    let positions = [];
    let colors = [];
    let onCenter = [];
    let initialAngles = [];
    let stage1Angles = [];
    let stage2Rotations = [];
    let stage3Rotations = [];
    let gradients = [];

    for (let i = 0; i < 12; i++) {
      positions.push(0, 0, 0);
    }

    let triangleLines = [
      ["l0", "l1"],
      ["l1", "l2"],
      ["l2", "l3"],
      ["l3", "l0"],
    ];

    triangleLines.forEach((tl) => {
      colors.push(0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5);
      onCenter.push(0, 0, 1);
      initialAngles.push(
        lines[tl[0]].initialAngle,
        lines[tl[1]].initialAngle,
        lines[tl[0]].initialAngle
      );
      stage1Angles.push(
        lines[tl[0]].stage1Angle,
        lines[tl[1]].stage1Angle,
        lines[tl[0]].stage1Angle
      );
      stage2Rotations.push(
        lines[tl[0]].stage2Rotation,
        lines[tl[1]].stage2Rotation,
        lines[tl[0]].stage2Rotation
      );
      stage3Rotations.push(
        lines[tl[0]].stage3Rotation,
        lines[tl[1]].stage3Rotation,
        lines[tl[0]].stage3Rotation
      );
    });

    gradients.push(
      0.2,
      0.3,
      1, // Triangle 0
      0,
      1,
      1, // Triangle 1
      0,
      0,
      1, // Triangle 2
      1,
      0,
      1 // Triangle 3
    );

    let geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(positions), 3)
    );
    geometry.setAttribute("aColor", new THREE.BufferAttribute(new Float32Array(colors), 3));
    geometry.setAttribute(
      "aOnCenter",
      new THREE.BufferAttribute(new Float32Array(onCenter), 1)
    );
    geometry.setAttribute(
      "aInitialAngle",
      new THREE.BufferAttribute(new Float32Array(initialAngles), 1)
    );
    geometry.setAttribute(
      "aStage1Angle",
      new THREE.BufferAttribute(new Float32Array(stage1Angles), 1)
    );
    geometry.setAttribute(
      "aStage2Rotation",
      new THREE.BufferAttribute(new Float32Array(stage2Rotations), 1)
    );
    geometry.setAttribute(
      "aStage3Rotation",
      new THREE.BufferAttribute(new Float32Array(stage3Rotations), 1)
    );
    geometry.setAttribute(
      "aGradient",
      new THREE.BufferAttribute(new Float32Array(gradients), 1)
    );

    return geometry;
  }, []);

  const titleGeometry = useMemo(() => {
    let positions = [];
    let uvs = [];
    let directions = [];

    for (let i = 0; i < 6; i++) {
      positions.push(0, 0, 0);
    }

    uvs.push(
      0,
      0, // p0
      1,
      0, // p1
      0,
      1, // p2
      1,
      0, // p1
      1,
      1, // p3
      0,
      1 // p2
    );

    directions.push(
      -1,
      -1, // p0
      1,
      -1, // p1
      -1,
      1, // p2
      1,
      -1, // p1
      1,
      1, // p3
      -1,
      1 // p2
    );

    let geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(positions), 3)
    );
    geometry.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(uvs), 2));
    geometry.setAttribute(
      "aDirection",
      new THREE.BufferAttribute(new Float32Array(directions), 2)
    );

    return geometry;
  }, []);

  useFrame(({ clock, pointer }) => {
    if (animation.current.completed) {
      let pointer = uniforms.current.uPointer.value.clone();
      let focus = uniforms.current.uFocus.value.clone();

      let easeFactor = 0.004;
      let range = 0.2;

      let distance = pointer.distanceTo(params.initialFocus);
      let d = new THREE.Vector2().subVectors(pointer, focus).normalize().multiplyScalar(range);
      d.add(params.initialFocus);

      let target = distance < range ? pointer : d;

      focus.x += (target.x - focus.x) * easeFactor;
      focus.y += (target.y - focus.y) * easeFactor;

      uniforms.current.uFocus.value = focus;
    }
  });

  // Timelines
  const timeline = useMemo(() => {
    let tl = gsap.timeline();
    let values = animation.current;

    tl.to(values, {
      progress1: 1,
      duration: 2.5,
      ease: "power2.out",
      onUpdate: () => {
        uniforms.current.uProgress1.value = values.progress1;
      },
    });

    tl.to(values, {
      progress2: 1,
      duration: 2,
      ease: "power2.in",
      onUpdate: () => {
        uniforms.current.uProgress2.value = values.progress2;
        uniforms.current.uTickAngle.value =
          lines.l4.stage1Angle + lines.l4.stage2Rotation * values.progress2;

        uniforms.current.uLine0Angle.value =
          lines.l0.stage1Angle + lines.l0.stage2Rotation * values.progress2;
        uniforms.current.uLine0Angle.value %= Math.PI * 2.0;
        uniforms.current.uLine1Angle.value =
          lines.l1.stage1Angle + lines.l1.stage2Rotation * values.progress2;
        uniforms.current.uLine1Angle.value %= Math.PI * 2.0;
        uniforms.current.uLine2Angle.value =
          lines.l2.stage1Angle + lines.l2.stage2Rotation * values.progress2;
        uniforms.current.uLine2Angle.value %= Math.PI * 2.0;
        uniforms.current.uLine3Angle.value =
          lines.l3.stage1Angle + lines.l3.stage2Rotation * values.progress2;
        uniforms.current.uLine3Angle.value %= Math.PI * 2.0;
      },
    });

    tl.to(values, {
      progress3: 1,
      duration: 2,
      ease: "power2.out",
      onUpdate: () => {
        uniforms.current.uProgress3.value = values.progress3;

        uniforms.current.uFocus.value = new THREE.Vector2(
          params.initialFocus.x + values.progress3 * params.moveFocus.x,
          params.initialFocus.y + values.progress3 * params.moveFocus.y
        );

        uniforms.current.uPointer.value = uniforms.current.uFocus.value.clone();

        uniforms.current.uTickAngle.value =
          lines.l4.stage1Angle +
          lines.l4.stage2Rotation +
          lines.l4.stage3Rotation * values.progress3;

        uniforms.current.uLine0Angle.value =
          lines.l0.stage1Angle +
          lines.l0.stage2Rotation +
          lines.l0.stage3Rotation * values.progress3;
        uniforms.current.uLine0Angle.value %= Math.PI * 2.0;

        uniforms.current.uLine1Angle.value =
          lines.l1.stage1Angle +
          lines.l1.stage2Rotation +
          lines.l1.stage3Rotation * values.progress3;
        uniforms.current.uLine1Angle.value %= Math.PI * 2.0;

        uniforms.current.uLine2Angle.value =
          lines.l2.stage1Angle +
          lines.l2.stage2Rotation +
          lines.l2.stage3Rotation * values.progress3;
        uniforms.current.uLine2Angle.value %= Math.PI * 2.0;

        uniforms.current.uLine3Angle.value =
          lines.l3.stage1Angle +
          lines.l3.stage2Rotation +
          lines.l3.stage3Rotation * values.progress3;
        uniforms.current.uLine3Angle.value %= Math.PI * 2.0;
      },

      onComplete: () => {
        values.completed = true;
      },
    });

    tl.fromTo(
      ".animated-el-1",
      { opacity: 0, y: -8, pointerEvents: "none" },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        pointerEvents: "auto",
        stagger: 0.1,
      },
      "-=1.0"
    );

    tl.fromTo(
      ".animated-el-2",
      { opacity: 0, y: 8, pointerEvents: "none" },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        pointerEvents: "auto",
        stagger: 0.1,
      },
      "-=1.0"
    );

    return tl;
  }, []);

  // Event listeners
  useEffect(() => {
    let resetButton = document.querySelector(".reset-button");
    resetButton.style.display = DEBUG || window.location.hash == "#panel" ? "block" : "none";

    resetButton.addEventListener("click", () => {
      timeline.progress(0);
      animation.current.progress1 = 0;
      animation.current.progress2 = 0;
      animation.current.progress3 = 0;
      animation.current.completed = false;
    });

    window.addEventListener("mousemove", (e) => {
      if (animation.current.completed) {
        let pointer = new THREE.Vector2(
          e.clientX / window.innerWidth - 0.5,
          -e.clientY / window.innerHeight + 0.5
        );
        uniforms.current.uPointer.value = pointer;
      }
    });

    window.addEventListener("touchmove", (e) => {
      if (animation.current.completed) {
        let touch = e.targetTouches[0];

        let pointer = new THREE.Vector2(
          touch.clientX / window.innerWidth - 0.5,
          -touch.clientY / window.innerHeight + 0.5
        );
        uniforms.current.uPointer.value = pointer;
      }
    });

    let onResize = () => {
      let ww = window.innerWidth;
      let titleWidth = ww < 768 ? ww * 0.7 : ww * 0.5;
      uniforms.current.uTitleWidth.value = titleWidth;
      uniforms.current.uSizes.value = new THREE.Vector2(size.width, size.height);
    };
    onResize();
    window.addEventListener("resize", onResize);
  }, []);

  // Control panel
  const controls = useControls("Colors", {
    lineColor: params.lineColor,
    initialColor: params.initialColor,
    gradientColor1: params.gradientColor1,
    gradientColor2: params.gradientColor2,
    gradientFactor: {
      value: params.gradientFactor,
      min: 0,
      max: 1,
      step: 0.01,
    },
  });

  useEffect(() => {
    uniforms.current.uLineColor.value = new THREE.Color(controls.lineColor);
    uniforms.current.uInitialColor.value = new THREE.Color(controls.initialColor);
    uniforms.current.uGradientColor1.value = new THREE.Color(controls.gradientColor1);
    uniforms.current.uGradientColor2.value = new THREE.Color(controls.gradientColor2);
  }, [controls]);

  return (
    <>
      {/* Lines */}
      <lineSegments geometry={lineGeometry} renderOrder={15}>
        <shaderMaterial
          vertexShader={lineVs}
          fragmentShader={lineFs}
          uniforms={uniforms.current}
          depthTest={false}
          transparent
        />
      </lineSegments>

      {/* Masking mesh */}
      <mesh geometry={meshGeometry} renderOrder={10}>
        <shaderMaterial
          vertexShader={maskingMeshVs}
          fragmentShader={maskingMeshFs}
          uniforms={uniforms.current}
          depthTest={false}
          transparent
        />
      </mesh>

      {/* Background mesh */}
      <mesh geometry={meshGeometry}>
        <shaderMaterial
          vertexShader={backgroundMeshVs}
          fragmentShader={backgroundMeshFs}
          uniforms={uniforms.current}
        />
      </mesh>

      {/* Title */}
      <mesh geometry={titleGeometry}>
        <shaderMaterial
          vertexShader={titleVs}
          fragmentShader={titleFs}
          uniforms={uniforms.current}
          depthTest={false}
          transparent
        />
      </mesh>

      {/* <points geometry={titleGeometry}>
        <shaderMaterial
          vertexShader={titleVs}
          fragmentShader={titleFs}
          uniforms={uniforms.current}
        />
      </points> */}

      {/* Helpers */}
      {DEBUG && (
        <>
          <mesh rotation-x={-Math.PI * 0.5}>
            <planeGeometry args={[2000, 2000, 20, 20]} />
            <meshBasicMaterial wireframe transparent opacity={0.1} />
          </mesh>

          <axesHelper args={[1000]} />
          <axesHelper args={[1000]} rotation-y={Math.PI} />

          <points geometry={lineGeometry}>
            <shaderMaterial
              vertexShader={lineVs}
              fragmentShader={lineFs}
              uniforms={uniforms.current}
              depthTest={false}
              transparent
            />
          </points>
        </>
      )}
    </>
  );
};

export default Scene;
